/**
 * Section 07 — Sniper Strategy (R129 rewrite).
 *
 * Dual-timeframe CLH contrarian-pullback strategy. An HTF trend gate sets the
 * side; three CTF wick/volume conditions time the entry; exits are the first
 * fresh opposite trend-cycle. Mirrors backend/services/strategies/sniperRules.ts
 * and the frontend overlay (utils/strategies/sniper.ts) verbatim.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_sniper: GuideSection = {
  id: 'strategy-sniper',
  label: 'Sniper Strategy',
  group: 'strategies',
  icon: <GpsFixedIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Sniper Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A <strong>dual-timeframe CLH contrarian-pullback</strong> strategy (R129 rewrite). A higher-timeframe
        trend gate decides the side; three lower-timeframe wick + volume-spike conditions time the entry into
        the pullback. The old CMid cloud-flip Sniper has been fully replaced by this engine.
      </Typography>

      <GuideCallout kind="info" title="One engine, three surfaces">
        The exact same rules run in three places, so they never diverge: the <strong>chart overlay</strong>{' '}
        (Strategies dropdown → Sniper — markers + position-state colouring), the <strong>scheduled / manual
        Scan</strong>, and the alert it emits for auto-trade. RockBand is the other in-house engine; pick
        whichever you've validated for a symbol.
      </GuideCallout>

      <H id="intuition">The intuition</H>
      <P>
        Trade <em>with</em> the higher-timeframe trend, but enter on a lower-timeframe <em>pullback against
        it</em> that shows exhaustion. When the HTF is up, Sniper waits for the CTF to dip and then buys the
        moment a volume spike signals the dip is being absorbed — a better price than chasing the trend. It's
        contrarian on the small timeframe, trend-following on the big one. SHORT is the mirror image.
      </P>

      <H id="bars">The bar model — CLH + dual timeframe</H>
      <P>Everything is computed on <strong>closed</strong> bars, after two transforms:</P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>CLH candles</strong> — Custom LinReg + Heikin-Ashi smoothing (the same candle type DailyBlast uses). Trend and wick reads are taken on these smoothed bars.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>CTF</strong> (current/scan timeframe) and <strong>HTF</strong> (higher timeframe, from Strategies → Timeframe Mapping). The HTF trend is mapped onto each CTF bar by the HTF bar that <em>contains</em> that CTF bar's start time.</>} />
        </ListItem>
      </List>
      <P>Three primitives drive every rule:</P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Trend</strong> (per timeframe) — <code>close &gt; prevClose</code> ⇒ up (+1); <code>&lt;</code> ⇒ down (−1); equal ⇒ carry the previous state.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Wick test</strong> — a wick whose length is ≤ <em>No-wick tolerance</em> (default 0.05% of the bar's range) counts as "no wick". So "no upper wick" / "has lower wick" etc. are robust on smoothed CLH bars.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Volume spike</strong> — a weighted-volume spike (<code>wVol &gt; slow×2</code>, the same Vol indicator on the chart). A <em>Bull</em> spike is a spike on an up raw candle; a <em>Bear</em> spike is a spike on a down raw candle.</>} />
        </ListItem>
      </List>

      <H id="entry-rules">Entry rules — the three BUY / SHORT conditions</H>
      <P>
        Sniper only takes a fresh entry when <strong>FLAT</strong> (one position per symbol; the strategy itself
        never flips side directly — though the auto-trade engine's auto-reverse can close-then-open on an
        opposite signal). The HTF gate makes BUY and SHORT mutually exclusive; conditions are tested
        <strong> C1 → C2 → C3, first match wins</strong>.
      </P>
      <H3>BUY — only when HTF is up (and ISL sits below entry)</H3>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Rule</TableCell><TableCell sx={{ fontWeight: 700 }}>Conditions</TableCell><TableCell sx={{ fontWeight: 700 }}>Idea</TableCell></TableRow></TableHead>
        <TableBody>
          <TableRow><TableCell><code>B_C1</code></TableCell><TableCell>HTF up · CTF down · no upper wick · Vol spike</TableCell><TableCell>Contrarian dip-buy — a clean down-bar into the trend on heavy volume.</TableCell></TableRow>
          <TableRow><TableCell><code>B_C2</code></TableCell><TableCell>HTF up · CTF down · has lower wick · Vol <em>Bull</em> spike</TableCell><TableCell>Rejection wick — the dip was bought back (lower wick) on bullish volume.</TableCell></TableRow>
          <TableRow><TableCell><code>B_C3</code></TableCell><TableCell>HTF up · CTF up · no lower wick · Vol <em>Bull</em> spike</TableCell><TableCell>Trend continuation — strong up-bar with no lower wick on bullish volume.</TableCell></TableRow>
        </TableBody>
      </Table>
      <H3>SHORT — only when HTF is down (and ISL sits above entry)</H3>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Rule</TableCell><TableCell sx={{ fontWeight: 700 }}>Conditions</TableCell><TableCell sx={{ fontWeight: 700 }}>Idea</TableCell></TableRow></TableHead>
        <TableBody>
          <TableRow><TableCell><code>S_C1</code></TableCell><TableCell>HTF down · CTF up · no lower wick · Vol spike</TableCell><TableCell>Contrarian rip-sell — a clean up-bar against the down-trend on heavy volume.</TableCell></TableRow>
          <TableRow><TableCell><code>S_C2</code></TableCell><TableCell>HTF down · CTF up · has upper wick · Vol <em>Bear</em> spike</TableCell><TableCell>Rejection wick — the rip was sold (upper wick) on bearish volume.</TableCell></TableRow>
          <TableRow><TableCell><code>S_C3</code></TableCell><TableCell>HTF down · CTF down · no upper wick · Vol <em>Bear</em> spike</TableCell><TableCell>Trend continuation — strong down-bar with no upper wick on bearish volume.</TableCell></TableRow>
        </TableBody>
      </Table>
      <P>
        The matched rule is carried as the signal's <code>reason</code> (<code>B_C1…S_C3</code>) and shown in
        the chart marker, the alert and the Signal Log.
      </P>

      <H id="isl-target">ISL &amp; Target</H>
      <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', p: 1.25, my: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.75 }}>{`BUY    ISL    = lowest low  of the previous 3 CLH bars   (must be < entry)
SHORT  ISL    = highest high of the previous 3 CLH bars   (must be > entry)
risk          = | entry − ISL |
Target        = entry ± N × risk        (N = Target Risk Multiple, default 2)`}</Box>
      <P>
        <code>N</code> is the same <strong>Target Risk Multiple</strong> as RockBand (Settings → Target tab).
        If the ISL would land on the wrong side of entry (no risk), the condition is skipped — Sniper never
        emits a zero/negative-risk signal.
      </P>

      <H id="exits">Exits — SELL / COVER</H>
      <P>
        Sniper manages its own exit off trend cycles (independent of the SL/Target, which the trade engine
        also enforces):
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>SELL</strong> (exit long) — the first <em>fresh</em> CTF down-trend that starts a <em>later</em> down-cycle than the one the BUY was taken in. A dip within the same down-move that triggered the buy won't exit you prematurely.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>COVER</strong> (exit short) — the mirror: the first fresh CTF up-trend starting a later up-cycle than the SHORT's.</>} />
        </ListItem>
      </List>
      <P>
        These emit as <code>SELL_FLAT</code> / <code>COVER_FLAT</code> actions. On the chart, bars are coloured
        by position state — green while LONG, red while SHORT, neutral while FLAT.
      </P>

      <H id="timeframe-mapping">Timeframe mapping</H>
      <P>
        The HTF that gates each scan/chart timeframe comes from <OpenInApp to="/strategies" label="Strategies → Timeframe Mapping" /> —
        the same mapping the chart's OverView HTF gauge uses. Lower CTF = more, faster signals (and more noise);
        higher CTF = fewer, slower swings. Sniper is happiest on the 5m–1h CTFs where the CLH smoothing and the
        volume-spike read are meaningful.
      </P>

      <H id="setup">How to enable Sniper</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Open <OpenInApp to="/strategies" label="Strategies → Sniper" /> and pick the watchlist(s) to scan (they're unioned).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Set the scan interval (CTF) and the scan cadence. Run <strong>Scan Now</strong> for an immediate pass, or enable the scheduled scan to leave it running (and optionally flip its <em>Auto-Trade this scan</em> toggle to route signals to auto-execution).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Watch the <strong>Signal Log</strong> — every emitted BUY/SHORT/SELL/COVER is logged with its firing rule (B_C1…S_C3) so you can audit.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>To see it on a chart, open the symbol, pick the same interval, and choose <strong>Sniper</strong> in the Strategy dropdown — the markers there are bar-for-bar identical to what the scan emits.</>} />
        </ListItem>
      </List>

      <GuideExample title="A worked Sniper BUY — B_C2 rejection wick">
        BTCUSD, 15m CTF with the 1h HTF up (higher-timeframe uptrend intact). Price has been pulling back: the
        last CLH bar closes <em>down</em> vs the prior close (CTF down), but it prints a clear <em>lower wick</em>
        — the dip got bought back — and weighted volume spikes on an up raw candle (Vol Bull spike). That's
        <strong> B_C2</strong>. Entry = the bar's close, say 67,300; ISL = the lowest low of the previous 3 CLH
        bars, say 66,900 (risk 400); with N = 2 → Target 68,100. The signal logs as "BUY · B_C2", the chart
        marker shows B, and (with Auto-Trade on for the scan + Auto Long on the symbol) it routes to execution —
        optionally parking for the 1-minute pullback if the Entry Qualifier is on. Sniper holds LONG until a
        fresh later down-cycle prints a SELL.
      </GuideExample>

      <H id="tuning">Tuning</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Target Risk Multiple (N)</strong> — Settings → Target tab (shared with RockBand). 2 = a 1:2 reward:risk target; raise for fewer, larger wins.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>No-wick tolerance %</strong> — default 0.05% of bar range. Larger = treats small wicks as "no wick" (looser wick gates, more signals).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Timeframe pair</strong> — change the CTF (scan interval) and the HTF it maps to (Timeframe Mapping). The HTF gate is the single biggest driver of how often Sniper finds a tradable side.</>} />
        </ListItem>
      </List>

      <GuideCallout kind="warning" title="When Sniper underperforms">
        It needs a real higher-timeframe trend to gate against. In a flat / range-bound HTF the gate rarely
        opens, so it simply takes fewer trades — but a choppy CTF inside a weak HTF trend can still whipsaw.
        Validate a symbol/timeframe on the chart overlay (and a backtest, when available) before letting it
        auto-trade.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'The intuition',       anchor: 'intuition',       body: 'sniper intuition htf trend gate ctf pullback contrarian dip buy rip sell' },
    { heading: 'Bar model — CLH + dual timeframe', anchor: 'bars', body: 'clh candle linreg heikin ashi ctf htf trend wick tolerance volume spike bull bear weighted volume' },
    { heading: 'Entry rules — BUY/SHORT conditions', anchor: 'entry-rules', body: 'sniper entry rules b_c1 b_c2 b_c3 s_c1 s_c2 s_c3 buy short conditions wick volume spike first match wins' },
    { heading: 'ISL & Target',        anchor: 'isl-target',      body: 'sniper isl lowest low highest high previous 3 clh target risk multiple n' },
    { heading: 'Exits — SELL / COVER',anchor: 'exits',           body: 'sniper exit sell cover fresh trend cycle position state colour' },
    { heading: 'Timeframe mapping',   anchor: 'timeframe-mapping', body: 'sniper timeframe mapping ctf htf scan interval' },
    { heading: 'How to enable',       anchor: 'setup',           body: 'sniper setup enable strategies scan signal log chart overlay' },
    { heading: 'Tuning',              anchor: 'tuning',          body: 'sniper tuning target risk multiple no wick tolerance timeframe pair' },
  ],
};

export default Section_strategy_sniper;
